import { adminUiCopy } from "@/copy/adminUiCopy";

const POPUP_BLOCKED_CODES = new Set(["auth/popup-blocked"]);
const POPUP_CLOSED_CODES = new Set([
  "auth/popup-closed-by-user",
  "auth/cancelled-popup-request",
]);
const STORAGE_UNAVAILABLE_CODES = new Set(["auth/web-storage-unsupported"]);
// Firebase persistence failures surface as plain `Error`s without a code.
const STORAGE_UNAVAILABLE_MESSAGE_PATTERN =
  /closing\/hidden|indexeddb|localstorage|web storage/iu;

const readErrorCode = (error: unknown): string | null =>
  typeof error === "object" &&
  error !== null &&
  "code" in error &&
  typeof (error as { code?: unknown }).code === "string"
    ? (error as { code: string }).code
    : null;

/**
 * Maps browser-side popup sign-in failures to actionable copy.
 *
 * @remarks
 * These failures are caused by the staff member's browser (blocked pop-ups,
 * a closed sign-in window, or blocked site storage such as private browsing),
 * not by the admin API, so the generic public-error formatter would only show
 * an internal message. Returns `null` for anything else so callers fall back
 * to the standard public-error path.
 */
export const resolvePopupLoginGuidance = (error: unknown): string | null => {
  const code = readErrorCode(error);
  if (code && POPUP_BLOCKED_CODES.has(code)) {
    return adminUiCopy.authLogin.popupBlocked;
  }
  if (code && POPUP_CLOSED_CODES.has(code)) {
    return adminUiCopy.authLogin.popupClosed;
  }
  if (code && STORAGE_UNAVAILABLE_CODES.has(code)) {
    return adminUiCopy.authLogin.storageUnavailable;
  }
  const message = error instanceof Error ? error.message : null;
  if (message && STORAGE_UNAVAILABLE_MESSAGE_PATTERN.test(message)) {
    return adminUiCopy.authLogin.storageUnavailable;
  }
  return null;
};
